import { useState } from 'react';
import { useDocuments } from '../lib/documents.js';
import { useLifeAreas, useObligations } from '../lib/obligations.js';
import { usePeople } from '../lib/people.js';
import { DocumentEditDrawer } from './DocumentEditDrawer.js';
import { DocumentPreview } from './DocumentPreview.js';
import { EditDrawer } from './EditDrawer.js';
import { ObligationPreview } from './ObligationPreview.js';
import { PersonEditSheet } from './PersonEditSheet.js';
import { PersonPreview } from './PersonPreview.js';

/**
 * The self-contained "open a person" surface: read-first card, with its
 * Edit action swapping to the shared edit sheet and back. Hosts anywhere
 * in the app (People page, a document card's About chips, the obligation
 * drawer's "View person" link) render this with just an id — no wiring
 * of the preview/editor pair at every call site.
 *
 * Drill-ins stay in place: tapping an occasion row or a key-document
 * row swaps this card for that entry's own preview (and its edit
 * drawer from there), and closing walks back to the person card —
 * never navigating away from the tool you were in. Only the "all
 * documents" actions leave, into the Documents by-person view.
 */
export function PersonCard({ personId, onClose }: { personId: string; onClose: () => void }) {
  const people = usePeople();
  const obligations = useObligations();
  const documents = useDocuments();
  const lifeAreas = useLifeAreas();
  const [editing, setEditing] = useState(false);
  // In-place drill-ins from the card's rows. `editingOccasion` /
  // `editingDocument` lift the respective edit drawers above their
  // previews (close-then-open pattern, like the pages do).
  const [occasionId, setOccasionId] = useState<string | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [editingOccasion, setEditingOccasion] = useState(false);
  const [editingDocument, setEditingDocument] = useState(false);

  // Read from the live cache so edits reflect immediately; if the person
  // was just deleted, there is nothing left to show.
  const person = people.data?.find((p) => p.id === personId);
  if (!person) return null;

  // ── Occasion drill-in (birthday / anniversary) ──────────────────────
  const occasion = occasionId
    ? (obligations.data?.find((o) => o.id === occasionId) ?? null)
    : null;
  if (occasion) {
    if (editingOccasion) {
      return (
        <EditDrawer
          obligation={occasion}
          onClose={() => setEditingOccasion(false)}
        />
      );
    }
    return (
      <ObligationPreview
        obligation={occasion}
        lifeArea={lifeAreas.data?.find((a) => a.id === occasion.lifeAreaId)}
        lifeAreas={lifeAreas.data ?? []}
        onClose={() => setOccasionId(null)}
        onEdit={() => setEditingOccasion(true)}
      />
    );
  }

  // ── Key-document drill-in ───────────────────────────────────────────
  const doc = documentId ? (documents.data?.find((d) => d.id === documentId) ?? null) : null;
  if (doc) {
    if (editingDocument) {
      return <DocumentEditDrawer target={doc} onClose={() => setEditingDocument(false)} />;
    }
    return (
      <DocumentPreview
        document={doc}
        lifeAreaName={lifeAreas.data?.find((a) => a.id === doc.lifeAreaId)?.name}
        onClose={() => setDocumentId(null)}
        onEdit={() => setEditingDocument(true)}
      />
    );
  }

  if (editing) {
    return (
      <PersonEditSheet
        target={person}
        onClose={() => setEditing(false)}
        onDeleted={onClose}
      />
    );
  }
  return (
    <PersonPreview
      person={person}
      onClose={onClose}
      onEdit={() => setEditing(true)}
      onOpenOccasion={setOccasionId}
      onOpenDocument={setDocumentId}
    />
  );
}
